
// Import necessary dependencies
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/lib/types';
import { transformRemoteToProduct, transformProductToRemote } from './transformers';
import { PRODUCTS_TABLE, getUserId } from './api-utils';

export const updateProductInApi = async (product: Product): Promise<Product> => {
  try {
    console.log('Updating product in API:', product.id);
    
    const userId = await getUserId();
    
    const remoteProduct = transformProductToRemote(product, userId);
    
    const { data, error } = await supabase
      .from(PRODUCTS_TABLE)
      .update(remoteProduct)
      .eq('id', product.id)
      .eq('user_id', userId) // Ensure we only update products owned by this user
      .select()
      .single();
    
    if (error) {
      console.error('Error updating product:', error);
      throw error;
    }
    
    console.log('Product updated successfully:', data.name);
    
    // Handle group relationships if they've changed
    if (product.groupIds !== undefined) {
      try {
        // First delete existing relationships
        const { error: deleteError } = await supabase
          .from('product_groups')
          .delete()
          .eq('product_id', product.id);
          
        if (deleteError) {
          console.error('Error deleting existing product-group relationships:', deleteError);
        }
        
        // Then add new relationships if there are any groups
        if (product.groupIds.length > 0) {
          const groupRelations = product.groupIds.map(groupId => ({
            product_id: product.id,
            group_id: groupId
          }));
          
          const { error: insertError } = await supabase
            .from('product_groups')
            .insert(groupRelations);
            
          if (insertError) {
            console.error('Error adding updated product-group relationships:', insertError);
          }
        }
      } catch (groupError) {
        console.error('Error updating product-group relationships:', groupError);
        // Continue anyway, just log the error
      }
    }
    
    // Return the complete product with groupIds
    const result = transformRemoteToProduct(data);
    result.groupIds = product.groupIds || [];
    return result;
  } catch (error) {
    console.error('Error in updateProductInApi:', error);
    throw error;
  }
};
